import { useState, useEffect, useCallback, createContext, useContext, useRef, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

// Eagerly loaded (always needed)
import Login from "./pages/Login";
import Sidebar from "./components/Sidebar";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { ThemeProvider } from "./components/ThemeProvider";
import QuickActionsFAB from "./components/QuickActionsFAB";
import OnboardingWizard from "./components/OnboardingWizard";
import { PinGate } from "./components/auth/PinGate";
import { BackgroundTranscriptionProvider } from "./contexts/BackgroundTranscriptionContext";
import { BackgroundNoteGenProvider } from "./contexts/BackgroundNoteGenContext";

// Lazy-loaded pages (loaded on-demand for faster initial render)
const Dashboard = lazy(() => import("./pages/Dashboard"));
const RecipeLibrary = lazy(() => import("./pages/RecipeLibrary"));
const CreateMealPlan = lazy(() => import("./pages/CreateMealPlan"));
const EditMealPlan = lazy(() => import("./pages/EditMealPlan"));
const MealPlanHistory = lazy(() => import("./pages/MealPlanHistory"));
const Templates = lazy(() => import("./pages/Templates"));
const ExerciseLibrary = lazy(() => import("./pages/ExerciseLibrary"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const SupplementLibrary = lazy(() => import("./pages/SupplementLibrary"));
const ComparePlans = lazy(() => import("./pages/ComparePlans"));
const NoteScriber = lazy(() => import("./pages/NoteScriber"));
const Documents = lazy(() => import("./pages/Documents"));
const ActivityTracker = lazy(() => import("./pages/ActivityTracker"));
const PatientLabs = lazy(() => import("./pages/PatientLabs"));
const PatientList = lazy(() => import("./pages/PatientList"));
const PatientDetail = lazy(() => import("./pages/PatientDetail"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const AiLearningDashboard = lazy(() => import("./pages/AiLearningDashboard"));
const Settings = lazy(() => import("./pages/Settings"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
// Use relative /api path so it works on any domain (preview and deployed)
const API = "/api";
// Full backend URL for static assets (images, file downloads)
export const ASSET_URL = BACKEND_URL;

// HIPAA: Session timeout (15 minutes idle, extended to 90 min during active recording)
const SESSION_TIMEOUT_MS = 15 * 60 * 1000;
const RECORDING_TIMEOUT_MS = 90 * 60 * 1000;
const WARNING_BEFORE_TIMEOUT_MS = 2 * 60 * 1000;

// Global flag for active recording sessions — prevents timeout during dictation
window.__scribeRecordingActive = false;

// Create axios instance
export const api = axios.create({
  baseURL: API,
});

// Add auth token interceptor - sends Bearer token on every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("session_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Treat API responses as activity (auto-save during recording keeps session alive)
api.interceptors.response.use((response) => {
  if (window.__lastActivityRef) window.__lastActivityRef.current = Date.now();
  return response;
}, (error) => Promise.reject(error));

// Retry interceptor for transient 5xx/network errors (e.g. Cloudflare 530)
api.interceptors.response.use(undefined, async (error) => {
  const config = error.config;
  const status = error.response?.status;
  if (!config || config._retryCount >= 2) return Promise.reject(error);
  // Don't retry on 503 (budget/auth), 504 (AI timeout), or blob responses (PDF downloads)
  if (status && status >= 500 && status !== 503 && status !== 504 && config.responseType !== "blob") {
    config._retryCount = (config._retryCount || 0) + 1;
    await new Promise(r => setTimeout(r, 1500 * config._retryCount));
    return api(config);
  }
  return Promise.reject(error);
});

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pinVerified, setPinVerified] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  window.__lastActivityRef = lastActivityRef;

  const checkAuth = useCallback(async () => {
    try {
      const response = await api.get("/auth/me");
      if (response.data?.token) {
        localStorage.setItem("session_token", response.data.token);
      }
      setUser(response.data);
      // If user has no PIN or PIN is already verified for this session, mark as verified
      if (!response.data?.pin_enabled || response.data?.pin_verified) {
        setPinVerified(true);
      } else {
        setPinVerified(false);
      }
      // Check if MFA is required for this user (per-user flag OR global setting)
      const userMfaRequired = response.data?.mfa_required || false;
      try {
        const mfaRes = await api.get("/admin/settings/require-mfa");
        setMfaRequired(userMfaRequired || mfaRes.data?.require_mfa || false);
      } catch {
        setMfaRequired(userMfaRequired);
      }
    } catch (error) {
      setUser(null);
      setPinVerified(false);
      localStorage.removeItem("session_token");
    } finally {
      setLoading(false);
    }
  }, []);

  // Called after user sets user data from login callback
  const setUserWithPinCheck = useCallback((userData) => {
    setUser(userData);
    if (!userData?.pin_enabled) {
      setPinVerified(true);
    } else {
      setPinVerified(false);
    }
  }, []);

  const onPinVerified = useCallback(() => {
    setPinVerified(true);
  }, []);

  const logout = useCallback(async (reason = null) => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    setPinVerified(false);
    localStorage.removeItem("session_token");
    if (reason === "timeout") {
      toast.warning("Session expired due to inactivity. Please log in again.", {
        duration: 5000,
      });
    }
  }, []);

  // HIPAA: Track user activity (throttled to prevent excessive re-renders)
  const updateActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    setShowTimeoutWarning(false);
    // Only update state every 60 seconds to avoid re-rendering the entire tree on every touch
    if (now - lastActivity > 60000) {
      setLastActivity(now);
    }
  }, [lastActivity]);

  // HIPAA: Session timeout management
  useEffect(() => {
    if (!user) return;

    const checkTimeout = () => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivityRef.current;
      const effectiveTimeout = window.__scribeRecordingActive ? RECORDING_TIMEOUT_MS : SESSION_TIMEOUT_MS;

      if (timeSinceActivity >= effectiveTimeout) {
        logout("timeout");
      } else if (timeSinceActivity >= effectiveTimeout - WARNING_BEFORE_TIMEOUT_MS) {
        setShowTimeoutWarning(true);
      }
    };

    // Check every 30 seconds
    const interval = setInterval(checkTimeout, 30000);

    // Track user activity
    const events = ["mousedown", "keydown", "touchstart", "scroll"];
    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    return () => {
      clearInterval(interval);
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, [user, logout, updateActivity]);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      setUser: setUserWithPinCheck, 
      loading, 
      checkAuth, 
      logout,
      pinVerified,
      onPinVerified,
      mfaRequired,
      showTimeoutWarning,
      extendSession: updateActivity
    }}>
      {children}
      {/* HIPAA: Session timeout warning */}
      {showTimeoutWarning && (
        <div className="fixed bottom-4 right-4 left-4 md:left-auto md:w-96 bg-yellow-50 border border-yellow-200 rounded-lg p-4 shadow-lg z-50">
          <p className="text-yellow-800 font-medium text-sm">Session Expiring Soon</p>
          <p className="text-yellow-700 text-xs mt-1">
            Your session will expire in 2 minutes due to inactivity.
          </p>
          <button
            onClick={updateActivity}
            className="mt-2 px-4 py-1.5 bg-yellow-600 text-white text-sm rounded-full hover:bg-yellow-700"
          >
            Stay Logged In
          </button>
        </div>
      )}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processedRef = useRef(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("Signing you in...");

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const hash = window.location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);

    if (sessionIdMatch) {
      const sessionId = sessionIdMatch[1];

      // Clear hash immediately to prevent re-processing
      window.history.replaceState(null, '', window.location.pathname);

      const attemptAuth = async (retries = 5) => {
        for (let attempt = 1; attempt <= retries; attempt++) {
          try {
            setStatus(attempt > 1 ? `Retrying authentication (${attempt}/${retries})...` : "Authenticating with server...");
            // Use fetch directly to bypass axios retry interceptors that cause duplicate retries on 530
            const fetchResp = await fetch(`${API}/auth/session`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ session_id: sessionId }),
            });
            if (!fetchResp.ok) {
              const errData = await fetchResp.json().catch(() => ({}));
              const err = new Error(errData.detail || `HTTP ${fetchResp.status}`);
              err.status = fetchResp.status;
              err.detail = errData.detail;
              throw err;
            }
            const data = await fetchResp.json();
            if (data?.token) {
              localStorage.setItem("session_token", data.token);
            }
            setStatus("Success! Redirecting...");
            setUser(data);
            navigate("/dashboard", { replace: true });
            return;
          } catch (err) {
            const status = err?.status || err?.response?.status;
            // Retry on 5xx/network errors, not on 4xx (auth failures)
            if (attempt < retries && (!status || status >= 500)) {
              const delay = Math.min(2000 * attempt, 8000);
              setStatus(`Connection issue — retrying in ${Math.round(delay/1000)}s (${attempt}/${retries})...`);
              await new Promise(r => setTimeout(r, delay));
              continue;
            }
            console.error("Auth callback error:", err);
            const detail = err?.detail || err?.response?.data?.detail || err?.message || "Unknown error";
            if (status === 403) {
              setError(detail);
            } else if (status === 429) {
              setError("Too many login attempts. Please wait a moment and try again.");
            } else {
              setError(`Login failed: ${detail}. Please try again.`);
            }
          }
        }
      };
      attemptAuth();
    } else {
      // Check if we have a token in localStorage (page refresh scenario)
      const stored = localStorage.getItem("session_token");
      if (stored) {
        navigate("/dashboard", { replace: true });
      } else {
        navigate("/login", { replace: true });
      }
    }
  }, [navigate, setUser]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="text-center max-w-md space-y-4">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-800">Login Issue</h2>
          <p className="text-slate-600 text-sm" data-testid="access-denied-msg">{error}</p>
          <p className="text-slate-400 text-xs">Origin: {window.location.origin} | API: /api (relative)</p>
          <button
            onClick={() => { localStorage.removeItem("session_token"); navigate("/login", { replace: true }); }}
            className="px-6 py-2 bg-forest text-white rounded-lg text-sm font-medium hover:bg-forest/90 transition-colors"
            data-testid="back-to-login-btn"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest mx-auto mb-4"></div>
        <p className="text-slate-600" data-testid="auth-callback-status">{status}</p>
      </div>
    </div>
  );
};

// Protected Route Component — enforces authentication AND PIN verification
const ProtectedRoute = ({ children }) => {
  const { user, loading, pinVerified, onPinVerified, mfaRequired } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // MFA enforcement: user has PIN enabled but hasn't verified it this session
  if (user.pin_enabled && !pinVerified) {
    return <PinGate user={user} onVerified={onPinVerified} />;
  }

  // MFA required globally but user hasn't set up PIN yet — force them to Settings (unless already there)
  if (mfaRequired && !user.pin_enabled && location.pathname !== "/settings") {
    return <Navigate to="/settings" state={{ mfaSetup: true }} replace />;
  }

  return children;
};

// Layout with Sidebar - Mobile Responsive
const DashboardLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 transition-colors duration-200">
      <Sidebar />
      <main className="lg:pl-[260px] min-h-screen pt-12 lg:pt-0">
        <div className="p-4 md:p-6 lg:p-8 xl:p-10">
          <Suspense fallback={
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-forest"></div>
            </div>
          }>
            {children}
          </Suspense>
        </div>
      </main>
      <QuickActionsFAB />
      <OnboardingWizard />
    </div>
  );
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Dashboard />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/recipes"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <RecipeLibrary />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/create-plan"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CreateMealPlan />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/meal-plans/:planId"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <EditMealPlan />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Templates />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/exercises"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ExerciseLibrary />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplements"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <SupplementLibrary />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/history"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <MealPlanHistory />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <AdminPanel />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/compare"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ComparePlans />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notes"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <NoteScriber />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/documents"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Documents />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/activity"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ActivityTracker />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/labs"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <PatientLabs />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/patients"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <PatientList />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/patients/:patientId"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <PatientDetail />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <AdminAnalytics />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-learning"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <AiLearningDashboard />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Settings />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <BackgroundTranscriptionProvider api={api}>
            <BackgroundNoteGenProvider api={api}>
              <AppRouter />
            </BackgroundNoteGenProvider>
          </BackgroundTranscriptionProvider>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
